import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import moment from 'moment';
import { formatSecondsToDHMS } from '../../utils/constant';

type CsvValue = string | number | null | undefined;

const escapeCell = (v: CsvValue): string => {
  if (v === null || v === undefined) return '';
  const s = String(v).replace(/"/g, '""');
  return /[",\n]/.test(s) ? `"${s}"` : s;
};

const downloadBlob = (
  data: string,
  filename: string,
  mime = 'text/csv;charset=utf-8;'
) => {
  const blob = new Blob([data], { type: mime });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const downloadReportByType = (
  type: number,
  items: any[],
  filenameBase: string
) => {
  let csv = '';
  switch (type) {
    case 1:
      csv = toAssignmentPivotCSV(items);
      break;
    case 2:
      // Build an Excel workbook with multiple sheets based on periods under games
      downloadExcelForType2(items, `${filenameBase}_${Date.now()}.xlsx`);
      return;
    default:
      csv = toGenericCSV(items);
  }
  const filename = `${filenameBase}_${Date.now()}.csv`;
  downloadBlob(csv, filename);
};

/*
Donload report functions based on report type
*/

// Generic CSV for simple array of objects
export const toGenericCSV = (items: any[]): string => {
  if (!Array.isArray(items) || items.length === 0) return '';
  const keySet = items.reduce((set: Set<string>, it: Record<string, any>) => {
    Object.keys(it || {}).forEach((k) => set.add(k));
    return set;
  }, new Set<string>());
  const keys: string[] = Array.from<string>(keySet);
  const header = keys.map((k) => escapeCell(k as CsvValue)).join(',');
  const rows = items.map((it: any) =>
    keys.map((k) => escapeCell((it as Record<string, CsvValue>)[k])).join(',')
  );
  return [header, ...rows].join('\n');
};

// Type 1 formatter: assignment pivot same as current page
export const toAssignmentPivotCSV = (items: any[]): string => {
  if (!Array.isArray(items) || items.length === 0) return '';

  const isoDatesSet = new Set<string>();
  for (const it of items) {
    const periods = it?.assignPeriods ?? [];
    for (const p of periods) {
      if (p?.date) isoDatesSet.add(String(p.date));
    }
  }
  const isoDates = Array.from(isoDatesSet).sort();

  const rowsMatrix: { name: string; counts: Record<string, number> }[] =
    items.map((it) => {
      const counts: Record<string, number> = {};
      for (const d of isoDates) counts[d] = 0;
      for (const p of it?.assignPeriods ?? []) {
        const d = String(p.date);
        if (d in counts) counts[d] += Number(p.periodCount || 0);
      }
      return { name: it?.name ?? '', counts };
    });

  const totals: Record<string, number> = {};
  for (const d of isoDates) totals[d] = 0;
  for (const r of rowsMatrix) {
    for (const d of isoDates) totals[d] += r.counts[d];
  }

  const fmt = (iso: string) => {
    const [y, m, d] = iso.split('-');
    return `${d}-${m}-${y}`;
  };

  const header = ['Name', ...isoDates.map(fmt), 'User total period'];
  const lines: string[] = [];
  lines.push(header.map(escapeCell).join(','));

  for (const r of rowsMatrix) {
    const perDates = isoDates.map((d) => r.counts[d]);
    const userTotal = perDates.reduce((sum, v) => sum + Number(v || 0), 0);
    const row = [r.name, ...perDates, userTotal];
    lines.push(row.map(escapeCell).join(','));
  }
  const totalRow = ['Total', ...isoDates.map((d) => totals[d]), ''];
  lines.push(totalRow.map(escapeCell).join(','));

  return lines.join('\n');
};

// Helper: Excel with 5 sheets for type 2 (games with periods)
const downloadExcelForType2 = (games: any[], fileName: string) => {
  const sampleData = Array.isArray(games) ? games : [];

  // 1) One row per period (flatten)
  const sheet1 = sampleData.flatMap((game: any) =>
    (game?.periods ?? []).map((p: any) => ({
      gameId: game.gameId || '-',
      priority: game.priority || '-',
      status: game.status || '-',
      assignedAt: game.assignedAt
        ? moment(game.assignedAt).format('DD-MM-YYYY HH:mm:ss')
        : '-',
      completedAt: game.completedAt
        ? moment(game.completedAt).format('DD-MM-YYYY HH:mm:ss')
        : '-',
      fetchedAt: game.fetchedAt
        ? moment(game.fetchedAt).format('DD-MM-YYYY HH:mm:ss')
        : '-',
      moderatorName: game.moderatorName || '-',
      moderatorTeam: game.moderatorTeam || '-',
      previousModeratorName: game.previousModeratorName || '-',
      qcModeratorName: game.qcModeratorName || '-',
      gameReassigneeName: game.gameReassigneeName || '-',
      gameAssigneeName: game.gameAssigneeName || '-',
      gameAssignmentType: game.gameAssignmentType || '-',
      token: p.token || '-',

      periodNo: p.periodNo || '-',
      taggerName: p.taggerName || '-',
      previousTaggerName: p.previousTaggerName || '-',

      taggingStartedAt: p.taggingStart
        ? moment(p.taggingStart).format('DD-MM-YYYY HH:mm:ss')
        : '-',
      taggingEndedAt: p.taggingEnd
        ? moment(p.taggingEnd).format('DD-MM-YYYY HH:mm:ss')
        : '-',
      taggingDuration:
        p?.taggingDuration === null || p?.taggingDuration === undefined
          ? '-'
          : formatSecondsToDHMS(p.taggingDuration),
    }))
  );

  // // 2) One row per game, merged periods string
  // const sheet2 = sampleData.map((game: any) => ({
  //   gameId: game.gameId || '-',
  //   priority: game.priority || '-',
  //   status: game.status || '-',
  //   assignedAt: game.assignedAt
  //     ? moment(game.assignedAt).format('DD-MM-YYYY HH:mm:ss')
  //     : '-',
  //   completedAt: game.completedAt
  //     ? moment(game.completedAt).format('DD-MM-YYYY HH:mm:ss')
  //     : '-',
  //   fetchedAt: game.fetchedAt
  //     ? moment(game.fetchedAt).format('DD-MM-YYYY HH:mm:ss')
  //     : '-',
  //   moderatorName: game.moderatorName || '-',
  //   moderatorTeam: game.moderatorTeam || '-',
  //   previousModeratorName: game.previousModeratorName || '-',
  //   qcModeratorName: game.qcModeratorName || '-',
  //   gameReassigneeName: game.gameReassigneeName || '-',
  //   gameAssigneeName: game.gameAssigneeName || '-',
  //   gameAssignmentType: game.gameAssignmentType || '-',
  //   periods: (game?.periods ?? [])
  //     .map(
  //       (p: any) =>
  //         `token = ${p.token || '-'} - Period = ${p.periodNo || '-'}  - Tagger Name = ${p.taggerName || '-'} - previousTaggerName = ${p.previousTaggerName || '-'} - Tagging Duration = ${p.taggingDuration ? formatSecondsToDHMS(p.taggingDuration || '-') : '-'} - Tagging Started At = ${p.taggingStart ? moment(p.taggingStart).format('DD-MM-YYYY HH:mm:ss') : '-'} - Tagging Ended At = ${p.taggingEnd ? moment(p.taggingEnd).format('DD-MM-YYYY HH:mm:ss') : '-'}`
  //     )
  //     .join(', '),
  // }));

  // // 3) Expanded columns up to N periods (detect max)
  // const maxPeriods = sampleData.reduce(
  //   (mx: number, g: any) => Math.max(mx, (g?.periods ?? []).length),
  //   0
  // );
  // const sheet3 = sampleData.map((game: any) => {
  //   const row: Record<string, CsvValue> = {
  //     gameId: game.gameId || '-',
  //     priority: game.priority || '-',
  //     status: game.status || '-',
  //     assignedAt: game.assignedAt
  //       ? moment(game.assignedAt).format('DD-MM-YYYY HH:mm:ss')
  //       : '-',
  //     completedAt: game.completedAt
  //       ? moment(game.completedAt).format('DD-MM-YYYY HH:mm:ss')
  //       : '-',
  //     fetchedAt: game.fetchedAt
  //       ? moment(game.fetchedAt).format('DD-MM-YYYY HH:mm:ss')
  //       : '-',
  //     moderatorName: game.moderatorName || '-',
  //     moderatorTeam: game.moderatorTeam || '-',
  //     previousModeratorName: game.previousModeratorName || '-',
  //     qcModeratorName: game.qcModeratorName || '-',
  //     gameReassigneeName: game.gameReassigneeName || '-',
  //     gameAssigneeName: game.gameAssigneeName || '-',
  //     gameAssignmentType: game.gameAssignmentType || '-',
  //   };
  //   const periods = game?.periods ?? [];
  //   for (let i = 0; i < maxPeriods; i++) {
  //     const p = periods[i];
  //     row[`Period${i + 1}_Number`] = p?.periodNo ?? '';
  //     row[`Period${i + 1}_Token`] = p?.token ?? '';
  //     row[`Period${i + 1}_Id`] = p?.periodId ?? '';
  //     row[`Period${i + 1}_Tagger`] = p?.taggerName ?? '';
  //     row[`Period${i + 1}_previousTaggerName`] = p?.previousTaggerName ?? '';
  //     row[`Period${i + 1}_taggingDuration`] = p.taggingDuration
  //       ? formatSecondsToDHMS(p.taggingDuration || '-')
  //       : '-';
  //     row[`Period${i + 1}_taggingStartedAt`] = p.taggingStart
  //       ? moment(p.taggingStart).format('DD-MM-YYYY HH:mm:ss')
  //       : '-';
  //     row[`Period${i + 1}_taggingEndedAt`] = p.taggingEnd
  //       ? moment(p.taggingEnd).format('DD-MM-YYYY HH:mm:ss')
  //       : '-';
  //   }
  //   return row;
  // });

  // // 4) Summary per game
  // const sheet4 = sampleData.map((game: any) => {
  //   // Group COMPLETED periods by (assign, taggerName)
  //   const periods: any[] = game?.periods ?? [];
  //   const summaryMap: Record<string, number> = periods.reduce(
  //     (acc: Record<string, number>, p: any) => {
  //       if (!p) return acc;
  //       // Treat a period as completed if it has an endTime
  //       const completed =
  //         p?.taggingEnd !== undefined &&
  //         p?.taggingEnd !== null &&
  //         String(p.taggingEnd) !== '';
  //       if (!completed) return acc;
  //       const key = `${String(p?.assign ?? '')}|${String(p?.taggerName ?? '')}`;
  //       acc[key] = (acc[key] || 0) + 1;
  //       return acc;
  //     },
  //     {}
  //   );

  //   const completedPeriodsByAssignTagger = Object.entries(summaryMap)
  //     .map(([k, count]) => {
  //       const [assign, taggerName] = k.split('|');
  //       return `Assign ${assign} - ${taggerName}: ${count}`;
  //     })
  //     .join(', ');

  //   return {
  //     gameId: game.gameId,
  //     status: game.status,
  //     moderatorName: game.moderatorName,
  //     totalPeriods: periods.length,
  //     totalAssign: periods.reduce(
  //       (sum: number, p: any) => sum + Number(p?.assign || 0),
  //       0
  //     ),
  //     completedDate: game.completedDate,
  //     // New: human-readable summary "assign taggerName totalPeriod" for completed periods
  //     completedPeriodsByAssignTagger: completedPeriodsByAssignTagger
  //       ? completedPeriodsByAssignTagger
  //       : 'No completed periods',
  //   };
  // });

  // // 5) Raw JSON for periods
  // const sheet5 = sampleData.map((game: any) => ({
  //   gameId: game.gameId || '-',
  //   priority: game.priority || '-',
  //   status: game.status || '-',
  //   assignedAt: game.assignedAt
  //     ? moment(game.assignedAt).format('DD-MM-YYYY HH:mm:ss')
  //     : '-',
  //   completedAt: game.completedAt
  //     ? moment(game.completedAt).format('DD-MM-YYYY HH:mm:ss')
  //     : '-',
  //   fetchedAt: game.fetchedAt
  //     ? moment(game.fetchedAt).format('DD-MM-YYYY HH:mm:ss')
  //     : '-',
  //   moderatorName: game.moderatorName || '-',
  //   moderatorTeam: game.moderatorTeam || '-',
  //   previousModeratorName: game.previousModeratorName || '-',
  //   qcModeratorName: game.qcModeratorName || '-',
  //   gameReassigneeName: game.gameReassigneeName || '-',
  //   gameAssigneeName: game.gameAssigneeName || '-',
  //   gameAssignmentType: game.gameAssignmentType || '-',
  //   'periods (JSON)': JSON.stringify(game?.periods ?? []),
  // }));

  // 6) Completed periods grouped by (assignId, taggerName) across ALL games
  const sheet6 = (() => {
    const globalMap: Record<string, number> = {};
    for (const game of sampleData) {
      const periods: any[] = game?.periods ?? [];
      for (const p of periods) {
        if (!p) continue;
        const completed =
          p?.taggingEnd !== undefined &&
          p?.taggingEnd !== null &&
          String(p.taggingEnd) !== '';
        if (!completed) continue;
        const key = `${String(p?.assign ?? '')}|${String(p?.taggerName ?? '')}`;
        globalMap[key] = (globalMap[key] || 0) + 1;
      }
    }
    return Object.entries(globalMap).map(([k, total]) => {
      const [assignId, taggerName] = k.split('|');
      return {
        assignId,
        taggerName,
        totalPeriodCompleted: total,
      };
    });
  })();

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.json_to_sheet(sheet1),
    'Full Report'
  );
  // XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sheet2), 'Merged');
  // XLSX.utils.book_append_sheet(
  //   wb,
  //   XLSX.utils.json_to_sheet(sheet3),
  //   'Expanded'
  // );
  // XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sheet4), 'Summary');
  // XLSX.utils.book_append_sheet(
  //   wb,
  //   XLSX.utils.json_to_sheet(sheet5),
  //   'Raw JSON'
  // );
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.json_to_sheet(sheet6),
    'Completed By Tagger'
  );

  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([wbout], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  saveAs(blob, fileName);
};
